import { StaticImageData } from 'next/image';
import { useRouter } from 'next/navigation';

import {
  Box,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Stack,
  Text,
  useTheme,
  VStack
} from '@chakra-ui/react';

import { Carousel, Divider } from 'ui';

interface InfoModalProps {
  isOpen: boolean;
  isResetDisabled?: boolean;
  title: string;
  description: string;
  continueText: string;
  banners: StaticImageData[];
  onClose: () => void;
  onContinueShopping: () => void;
  onResetShopping: () => void;
}

const ContinueShoppingModal = ({
  isOpen,
  onClose,
  title,
  continueText,
  onContinueShopping,
  isResetDisabled = false,
  onResetShopping,
  description,
  banners
}: InfoModalProps) => {
  const router = useRouter();
  const {
    colors: {
      palette: {
        components: { continueShoppingModal }
      }
    }
  } = useTheme();

  const handleStartOver = () => {
    onResetShopping();
    onClose();
  };

  const handleBack = () => {
    router.back();
    onResetShopping();
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size={{ base: '3xl', md: '6xl' }}
      isCentered
    >
      <ModalOverlay
        bg={continueShoppingModal.overlayBackground}
        opacity='0.1'
      />
      <ModalContent borderRadius='12px' overflow='hidden' margin='15px'>
        <ModalBody p={0}>
          <Flex gap={0} justifyContent='space-between'>
            <VStack
              alignItems='center'
              justifyContent='center'
              w='100%'
              gap={{ base: '30px', sm: '40px', md: '62px' }}
              py={{ base: '40px', md: '62px' }}
              px={{ base: '30px', sm: '50px', md: '60px', lg: '80px' }}
            >
              <Text
                fontSize={{ base: '16px', md: '18px', lg: '21px' }}
                color={continueShoppingModal.titleColor}
                textTransform='uppercase'
              >
                {title}
              </Text>

              <Divider
                w='100%'
                borderBottomColor={continueShoppingModal.dividerColor}
              />
              <Text
                fontSize={{ base: '24px', md: '28px', lg: '34px' }}
                fontWeight='semibold'
                lineHeight='160%'
                color={continueShoppingModal.descriptionColor}
                maxW='460px'
                textAlign='center'
              >
                {description}
              </Text>
              <Divider
                w='100%'
                borderBottomColor={continueShoppingModal.dividerColor}
              />

              <Stack gap='40px'>
                <Flex
                  w='100%'
                  justifyContent='center'
                  mt='auto'
                  gap='31px'
                  flexDirection={{ base: 'column', md: 'row' }}
                >
                  <Button
                    w='215px'
                    variant='outline'
                    colorScheme='black'
                    height='45px'
                    fontSize='14px'
                    onClick={onContinueShopping}
                  >
                    {continueText}
                  </Button>
                  <Button
                    w='215px'
                    variant='solid'
                    height='45px'
                    colorScheme='black'
                    fontSize='14px'
                    isDisabled={isResetDisabled}
                    onClick={handleStartOver}
                  >
                    Start Over
                  </Button>
                </Flex>

                <Button
                  variant='link'
                  onClick={handleBack}
                  color={continueShoppingModal.backBtnColor}
                  textTransform='uppercase'
                  fontSize='14px'
                  _hover={{
                    textDecoration: 'none'
                  }}
                >
                  Back
                </Button>
              </Stack>
            </VStack>
            <Box
              display={{ base: 'none', lg: 'block' }}
              maxW='345px'
              overflow='hidden'
              width='100%'
              marginRight='-3px'
              sx={{
                '& .slick-dots': {
                  bottom: '30px',
                  '& li': {
                    button: {
                      width: '10px',
                      height: '10px',
                      border: '1px solid',
                      borderColor: 'transparent',
                      borderRadius: '50%',
                      background: 'transparent',
                      _before: {
                        display: 'none'
                      }
                    }
                  }
                }
              }}
            >
              <Carousel
                banners={banners}
                settings={{
                  dots: true
                }}
              />
            </Box>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default ContinueShoppingModal;
